import type { FC } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { ToggleChip } from '@/components/ui/toggle-chip';
import { useTranslation } from '@/hooks/useTranslation';
import { RELATIVE_CHIPS, type RelativeChipKey, type TimeMode, type RangeError } from '../lib/signalTimeRange';

interface SignalsTimeRangeSelectorProps {
  mode: TimeMode;
  relativeChip: RelativeChipKey;
  onSelectRelative: (key: RelativeChipKey) => void;
  onSelectCustom: () => void;
  customStart: string;
  customEnd: string;
  onCustomStartChange: (value: string) => void;
  onCustomEndChange: (value: string) => void;
  rangeError: RangeError | null;
  lastTime: string | null;
  onUseLastActivity: () => void;
}

export const SignalsTimeRangeSelector: FC<SignalsTimeRangeSelectorProps> = ({
  mode,
  relativeChip,
  onSelectRelative,
  onSelectCustom,
  customStart,
  customEnd,
  onCustomStartChange,
  onCustomEndChange,
  rangeError,
  lastTime,
  onUseLastActivity,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-foreground">{t('signals.panel.timeRangeLabel')}</span>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('signals.panel.timeRangeLabel')}>
        {RELATIVE_CHIPS.map((chip) => (
          <ToggleChip
            key={chip.key}
            pressed={mode === 'relative' && relativeChip === chip.key}
            data-testid={`time-chip-${chip.key}`}
            onClick={() => onSelectRelative(chip.key)}
          >
            {t(`signals.panel.chips.${chip.key}`)}
          </ToggleChip>
        ))}
        <ToggleChip
          pressed={mode === 'custom'}
          data-testid="time-chip-custom"
          onClick={onSelectCustom}
        >
          {t('signals.panel.chips.custom')}
        </ToggleChip>
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        onClick={onUseLastActivity}
        disabled={!lastTime}
        data-testid="use-last-activity-button"
      >
        {t('signals.panel.lastActivityShortcut')}
      </Button>

      {mode === 'custom' && (
        <div className="flex flex-col gap-2 border-t border-dashed border-border pt-2">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="signals-range-start" className="text-xs font-semibold text-foreground">
                {t('signals.panel.customStartLabel')}
              </label>
              <Input
                id="signals-range-start"
                type="datetime-local"
                value={customStart}
                onChange={(e) => onCustomStartChange(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="signals-range-end" className="text-xs font-semibold text-foreground">
                {t('signals.panel.customEndLabel')}
              </label>
              <Input
                id="signals-range-end"
                type="datetime-local"
                value={customEnd}
                onChange={(e) => onCustomEndChange(e.target.value)}
              />
            </div>
          </div>
          {rangeError && rangeError !== 'incomplete' && (
            <Callout size="inline" tone="error" data-testid="range-error">
              {t(`signals.panel.rangeError.${rangeError}`)}
            </Callout>
          )}
        </div>
      )}
    </div>
  );
};
